/**
 * THEATER/DANCE (F4) — PURE logic of works and scene elements.
 *
 * A WORK (`scene_work`) is a script (theater, kind `script`) or a
 * choreography (dance, kind `choreography`); the ELEMENTS (`scene_element`)
 * are props (`prop`), costumes (`costume`) or roles/functions (`role`) and
 * can live free in the group or linked to a work. The group's category
 * decides the default work kind — never the proper name (gotcha from
 * products/agape.md §9).
 */

export const SCENE_ELEMENT_KINDS = ["prop", "costume", "role"] as const;
export type SceneElementKind = (typeof SCENE_ELEMENT_KINDS)[number];

/** Normalizes a free-text `kind` to a known type (default `prop`). */
export function toElementKind(kind: string | null | undefined): SceneElementKind {
  switch (kind) {
    case "costume": {
      return "costume";
    }
    case "role": {
      return "role";
    }
    default: {
      return "prop";
    }
  }
}

export const SCENE_WORK_KINDS = ["script", "choreography"] as const;
export type SceneWorkKind = (typeof SCENE_WORK_KINDS)[number];

/** Structural row of `scene_element` (what the logic uses). */
export interface SceneElementLike {
  id: string;
  work_id: string | null;
  kind: string | null;
  name: string | null;
  quantity: number | null;
  assigned_person_id: string | null;
  assigned_name: string | null;
  deleted_at?: string | null;
}

/** Work kind by the group's CATEGORY (dance → choreography; otherwise script). */
export function workKindForCategory(categoryId: string | null | undefined): SceneWorkKind {
  return categoryId === "dance" ? "choreography" : "script";
}

function alive<T extends SceneElementLike>(elements: readonly T[]): T[] {
  return elements.filter((e) => !e.deleted_at);
}

/** Elements linked to a work, ordered by kind and name. */
export function elementsOfWork<T extends SceneElementLike>(
  elements: readonly T[],
  workId: string,
): T[] {
  return alive(elements)
    .filter((e) => e.work_id === workId)
    .toSorted(
      (a, b) =>
        (a.kind ?? "").localeCompare(b.kind ?? "") || (a.name ?? "").localeCompare(b.name ?? ""),
    );
}

/** Free-standing elements of the group (ministry store, no work). */
export function unlinkedElements<T extends SceneElementLike>(elements: readonly T[]): T[] {
  return alive(elements)
    .filter((e) => !e.work_id)
    .toSorted((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
}

export interface SceneReadiness {
  total: number;
  assigned: number;
  byKind: Record<SceneElementKind, { total: number; assigned: number }>;
  /** Ready for stage: all ROLES have an assigned person. */
  ready: boolean;
}

/**
 * Readiness of a work: counts elements and assignments by kind. `ready`
 * requires that all roles (`role`) have a person; props/costumes do not
 * block (they can exist without an owner).
 */
export function sceneReadiness(elements: readonly SceneElementLike[]): SceneReadiness {
  const rows = alive(elements);
  const byKind: SceneReadiness["byKind"] = {
    prop: { total: 0, assigned: 0 },
    costume: { total: 0, assigned: 0 },
    role: { total: 0, assigned: 0 },
  };
  let assigned = 0;
  for (const e of rows) {
    const kind = toElementKind(e.kind);
    byKind[kind].total += 1;
    if (e.assigned_person_id) {
      byKind[kind].assigned += 1;
      assigned += 1;
    }
  }
  return {
    total: rows.length,
    assigned,
    byKind,
    ready: byKind.role.total > 0 && byKind.role.assigned === byKind.role.total,
  };
}
